import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook for managing Rule Form state, live previews, and Quick Add Category logic.
 */
export const useRuleForm = (activeProject, accounts, plTypes, applyRule, fetchRules, fetchSuggestions, fetchPayees) => {
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [previewTxs, setPreviewTxs] = useState([]);
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    transactionType: 'All',
    logicalOperator: 'AND',
    conditions: [
        { field: 'description', operator: 'contains', value: '' }
    ],
    targetAccountId: '',
    targetPayeeId: '',
    targetClassId: '',
    newCategoryName: '',
    newCategoryType: 'Expenses',
    newPayeeName: '',
    newPayeeType: 'Vendor',
    autoAdd: false
  });

  // Preview logic
  useEffect(() => {
    const fetchPreview = async () => {
      // Validate conditions before fetching
      const validConditions = ruleFormData.conditions.filter(c => c.value.trim().length > 0);
      if (!activeProject?._id || validConditions.length === 0) {
        setPreviewTxs([]);
        return;
      }

      try {
        const res = await axios.get(`/api/projects/${activeProject._id}/transactions?limit=1000`);
        const allTransactions = res.data.transactions || (Array.isArray(res.data) ? res.data : []);
        
        const matching = allTransactions.filter(tx => {
          if (tx.toAccount) return false;
          
          // Type filter
          let typeMatch = true;
          if (ruleFormData.transactionType === 'Money In' && tx.amount < 0) typeMatch = false;
          if (ruleFormData.transactionType === 'Money Out' && tx.amount > 0) typeMatch = false;
          if (!typeMatch) return false;

          // Logical filter
          const results = validConditions.map(cond => {
            const field = cond.field || 'description';
            const op = cond.operator;
            const val = cond.value.toUpperCase();
            const txVal = String(tx[field] || '').toUpperCase();
            const txAmt = Math.abs(tx.amount);
            const condAmt = parseFloat(cond.value);

            if (field === 'description') {
              if (op === 'contains') return txVal.includes(val);
              if (op === 'is exactly') return txVal === val;
              if (op === 'does not contain') return !txVal.includes(val);
            } else if (field === 'amount') {
              if (op === 'is greater than') return txAmt > condAmt;
              if (op === 'is less than') return txAmt < condAmt;
              return txAmt === condAmt;
            }
            return false;
          });

          if (ruleFormData.logicalOperator === 'OR') {
            return results.some(r => r === true);
          } else {
            return results.every(r => r === true);
          }
        });
        setPreviewTxs(matching.slice(0, 5));
      } catch (err) { console.error('Error fetching preview:', err); }
    };
    fetchPreview();
  }, [ruleFormData.conditions, ruleFormData.transactionType, ruleFormData.logicalOperator, activeProject?._id]);

  const addCondition = () => {
    setRuleFormData({
        ...ruleFormData,
        conditions: [...ruleFormData.conditions, { field: 'description', operator: 'contains', value: '' }]
    });
  };

  const removeCondition = (index) => {
    if (ruleFormData.conditions.length <= 1) return;
    const newConditions = ruleFormData.conditions.filter((_, i) => i !== index);
    setRuleFormData({ ...ruleFormData, conditions: newConditions });
  };

  const updateCondition = (index, field, val) => {
    const newConditions = [...ruleFormData.conditions];
    newConditions[index][field] = val;
    setRuleFormData({ ...ruleFormData, conditions: newConditions });
  };

  const openAdvancedRuleCreator = (selectedSuggestion, quickTargetId) => {
    setEditingRuleId(null);
    setRuleFormData({
      name: `Rule for ${selectedSuggestion.description}`,
      transactionType: selectedSuggestion.totalAmount > 0 ? 'Money In' : 'Money Out',
      logicalOperator: 'AND',
      conditions: [
          { field: 'description', operator: 'contains', value: selectedSuggestion.description }
      ],
      targetAccountId: quickTargetId || (accounts.find(a => plTypes.includes(a.type))?._id || ''),
      newCategoryName: '',
      newCategoryType: selectedSuggestion.totalAmount > 0 ? 'Income' : 'Expenses',
      newPayeeName: '',
      newPayeeType: 'Vendor',
      autoAdd: false
    });
    setShowRuleModal(true);
  };

  const openEditRuleCreator = (rule) => {
    setEditingRuleId(rule._id);
    setRuleFormData({
        name: rule.name,
        transactionType: rule.transactionType,
        logicalOperator: rule.logicalOperator || 'AND',
        conditions: rule.conditions && rule.conditions.length > 0 
            ? rule.conditions 
            : [{ field: 'description', operator: 'contains', value: rule.conditionValue || '' }],
        targetAccountId: rule.targetAccountId?._id || rule.targetAccountId || '',
        targetPayeeId: rule.targetPayeeId?._id || rule.targetPayeeId || '',
        targetClassId: rule.targetClassId?._id || rule.targetClassId || '',
        newCategoryName: '',
        newCategoryType: 'Expenses',
        newPayeeName: '',
        newPayeeType: 'Vendor',
        autoAdd: rule.autoAdd || false
    });
    setShowRuleModal(true);
  };

  const handleUpdateRule = async (e) => {
    if (e) e.preventDefault();
    try {
      let targetId = ruleFormData.targetAccountId;
      if (targetId === 'NEW_CATEGORY') {
        const accRes = await axios.post(`/api/projects/${activeProject._id}/accounts`, {
          name: ruleFormData.newCategoryName,
          type: ruleFormData.newCategoryType
        });
        targetId = accRes.data._id;
      }

      let payeeId = ruleFormData.targetPayeeId;
      if (payeeId === 'NEW_PAYEE') {
        const payeeRes = await axios.post(`/api/projects/${activeProject._id}/payees`, {
          name: ruleFormData.newPayeeName,
          type: ruleFormData.newPayeeType || 'Vendor'
        });
        payeeId = payeeRes.data._id;
        if (fetchPayees) {
          fetchPayees(activeProject._id, { all: 'true' });
        }
      }

      await axios.put(`/api/projects/${activeProject._id}/rules/${editingRuleId}`, {
        ...ruleFormData,
        targetAccountId: targetId,
        targetPayeeId: payeeId
      });

      if (window.confirm(`Rule updated! Apply to existing matching transactions?`)) {
        await applyRule(editingRuleId);
      } else {
        fetchRules();
        fetchSuggestions();
      }
      setShowRuleModal(false);
      setEditingRuleId(null);
      return true;
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.msg || 'Error updating rule');
      return false;
    }
  };

  const handleSaveAdvancedRule = async (e) => {
    if (e) e.preventDefault();
    try {
      let targetId = ruleFormData.targetAccountId;

      // Handle Quick Add Category
      if (targetId === 'NEW_CATEGORY') {
        const accRes = await axios.post(`/api/projects/${activeProject._id}/accounts`, {
          name: ruleFormData.newCategoryName,
          type: ruleFormData.newCategoryType
        });
        targetId = accRes.data._id;
      }

      let payeeId = ruleFormData.targetPayeeId;
      // Handle Quick Add Payee
      if (payeeId === 'NEW_PAYEE') {
        const payeeRes = await axios.post(`/api/projects/${activeProject._id}/payees`, {
          name: ruleFormData.newPayeeName,
          type: ruleFormData.newPayeeType || 'Vendor'
        });
        payeeId = payeeRes.data._id;
        if (fetchPayees) {
          fetchPayees(activeProject._id, { all: 'true' });
        }
      }

      const res = await axios.post(`/api/projects/${activeProject._id}/rules`, {
        ...ruleFormData,
        targetAccountId: targetId,
        targetPayeeId: payeeId
      });

      if (window.confirm(`Rule saved! Apply to existing matching transactions?`)) {
        await applyRule(res.data._id);
      } else {
        fetchRules();
        fetchSuggestions();
      }
      setShowRuleModal(false);
      return true;
    } catch (err) { 
      console.error(err); 
      alert(err.response?.data?.msg || 'Error saving rule');
      return false;
    }
  };

  return {
    showRuleModal, setShowRuleModal,
    editingRuleId,
    ruleFormData, setRuleFormData,
    previewTxs,
    openAdvancedRuleCreator,
    openEditRuleCreator,
    handleSaveAdvancedRule,
    handleUpdateRule,
    addCondition,
    removeCondition,
    updateCondition
  };
};
